/**
 * Holds a pending "open this draft" request from the agent's email.openComposer
 * intent. Callable from non-React code via getState().requestOpen(); the inbox
 * page's useAgentDraftOpen hook fulfills it. The monotonic requestId lets a
 * repeated draft re-fire the consumer; the atomic claim guarantees exactly-once
 * open even if more than one consumer instance is mounted (responsive dual-render).
 */
import { create } from 'zustand';

interface EmailComposeRequestState {
  requestId: number;
  claimedId: number;
  draftId: string | null;
  requestOpen: (draftId: string) => void;
  /**
   * Atomically claim the current request. Returns true to ONLY the first caller
   * of the current requestId (and clears draftId); returns false to later
   * callers and to stale ids. Guarantees exactly-once open.
   */
  claim: (id: number) => boolean;
}

export const useEmailComposeRequestStore = create<EmailComposeRequestState>((set) => ({
  requestId: 0,
  claimedId: 0,
  draftId: null,
  requestOpen: (draftId) => set((s) => ({ requestId: s.requestId + 1, draftId })),
  claim: (id) => {
    let won = false;
    set((s) => {
      if (id !== s.requestId || s.claimedId === id) return s;
      won = true;
      return { claimedId: id, draftId: null };
    });
    return won;
  },
}));
